<template>
    <div class="container-fluid luminairs-selection-tool-container" :class="{'loading': loading}">
        <div class="row">
            <!-- SLIDER COL START -->
            <div class="col-md-6 order-md-2 col-lg-7 bg-dark m-0 p-0 lst-slider-wrapper">
                <luminaire-line-slider />
            </div>
            <!-- SLIDER COL END -->

            <!-- KONFIGURATOR COL START-->
            <div class="col-md-6 order-md-1 col-lg-5 vh-100 pe-4 bg-dark lst-filter-wrapper">

                <!-- HEADING -->
                <div class="container-fluid mb-4 px-0">
                    <div class="row">
                        <div class="col-md-12">
                            <h2 class="featurette-heading text-white" v-if="product_configurator">{{ product_configurator.title }} {{ $t('configurator') }}</h2>
                        </div>
                    </div>
                </div>

                <!-- STEP NAVIGATION -->
                <luminaire-line-step-navigation
                    :product_configurator_id="product_configurator_id"
                    :current_step="current_step"
                    :clickableSteps="clickableSteps"
                    :editPlanning="true"
                    @update:current_step="updateCurrentStep" />

                <!-- PROJECT OVERVIEW -->
                <luminaire-line-project-overview
                    :product_configurator_id="product_configurator_id"
                    :generatedProductName="generatedProductName"
                    :generatedSku="generatedSku"
                    :calculatedProductPrice="calculatedProductPrice"
                    :finalPlanningPrice="finalPlanningPrice"
                    :luminaireLinePrices="luminaireLinePrices"
                    :specialProducts="luminaireLineFormFields.special_products"
                    :displayLedModule="true"
                    :totalLuminationLength="this.luminaireLineFormFields.total_lumination_line_length"
                />

                <!-- Configurator Steps Content-->
                <div class="tab-content" id="configurator-steps-content">

                    <!-- LUMINAIRE SELECTION TOOL Step -->
                    <luminaire-selection-tool v-if="product_configurator_id === 1"
                        :product_configurator_id="product_configurator_id"
                        :current_step="current_step"
                        :validatedSteps="validatedSteps"
                        :editPlanning="true"
                        :ledModuleConfiguration="ledModuleConfiguration"
                        :existingSku='this.planningFormFields.sku_of_led_module'
                        @update-loading="updateLoading"
                        @update-selected-serie="updateSelectedSerie"
                        @update-selected-control="updateSelectedControl"
                        @update-selected-housing-color="updateSelectedHousingColor"
                        @update-selected-optics="updateSelectedOptics"
                        @update-selected-protection-class="updateSelectedProtectionClass"
                        @update-selected-options="updateSelectedOptions"
                        @update-generated-sku="updateGeneratedSku"
                        @update-generated-product-name="updateGeneratedProductName"
                        @update-luminaire-control-reset="luminaireControlReset"
                        @update-step-1-validated="updateValidatedSteps"
                        @update-selected-optics-partial="updateSelectedOpticsPartial"
                        @update-individual-optic-selected="updateIndividualOpticSelected"
                        @update-luminaire-line-products="updateLuminaireLineProducts"
                        @update-lst-form="updateLstForm"
                        @update-all-luminaire-selection-steps-selected="updateAllLuminaireSelectionStepsSelected" />

                    <!-- LUMINAIRE LINE TOOL Step  -->
                    <luminaire-line-tool v-if="filteredLuminaireLineProducts"
                        :product_configurator_id="product_configurator_id"
                        :current_step="current_step"
                        :luminaireLineProducts="filteredLuminaireLineProducts"
                        :lstForm="lstForm"
                        :planning_id="planningFormFields.id"
                        :planningFormFields="planningFormFields"
                        :generatedSku="generatedSku"
                        :generatedProductName="generatedProductName"
                        :calculatedProductPrice="calculatedProductPrice"
                        :editPlanning="true"
                        :selectedSerie="selectedSerie"
                        :selectedControl="selectedControl"
                        :selectedHousingColor="selectedHousingColor"
                        :selectedOptions="selectedOptions"
                        :selectedOptics="selectedOptics"
                        :selectedProtectionClass="selectedProtectionClass"
                        :isIndividualOpticSelected="isIndividualOpticSelected"
                        :selectedOpticsPartial="selectedOpticsPartial"
                        :unfilteredProducts="products"
                        :lltDataLoaded="lltDataLoaded"
                        @update-ltt-form="updateLuminaireLineFormFields"
                        @update-final-planning-price="updateFinalPlanningPrice"
                        @update-luminaire-line-prices="updateLuminaireLinePrices"
                        @update-step-3-validated="updateValidatedSteps" />

                    <luminaire-line-form-navigation
                        :product_configurator_id="product_configurator_id"
                        :current_step="current_step"
                        :clickableSteps="clickableSteps"
                        :validatedSteps="validatedSteps"
                        :planningFormFields="planningFormFields"
                        :luminaireLineFormFields="luminaireLineFormFields"
                        :lstForm="lstForm"
                        :editPlanning="true"
                        :generatedSku="generatedSku"
                        :calculatedProductPrice="calculatedProductPrice"
                        :allLuminaireSelectionStepsSelected="allLuminaireSelectionStepsSelected"
                        @update-current-step="updateCurrentStep"
                        @update-loading="updateLoading"
                        @update-planning-form-errors="updatePlanningFormErrors"
                        @update-planning-created="updatePlanningCreated"
                        @set-planning-id="setPlanningId"
                        @get-projects="getProjects" />

                </div>
                <footer class="d-block d-md-none">
                    <p class="fs-6 py-2 px-4 m-0 text-center default-footer bg-dark text-white shadow">© {{ new Date().getFullYear() }} tempLED GmbH · <a href="https://templed.de/datenschutz/" target="_blank">{{ $t('Privacy policy') }}</a> · <a href="https://templed.de/impressum/" target="_blank">{{ $t('Imprint') }}</a> · Powered by <a href="https://des21.com/" target="_blank">des21</a></p>
                </footer>
            </div>
            <!-- KONFIGURATOR COL END -->

        </div>
    </div>
</template>

<script>

    import { mapGetters } from 'vuex';
    import { getFilteredLuminaireLineProductsExt } from "../../services/FilterProductsService";
    import { getLedPriceExt } from "../../services/LedPriceService";

    export default {
        data() {
            return {

                /**
                 * GENERAL DATA
                 **/
                product_configurator_id: null,
                product_configurator: null,
                loading: true,
                loadingError: false,
                current_step: 1,
                max_step: 3,
                validatedSteps: [],

                products: [],

                /**
                 * LUMINAIRE SELECTION TOOL DATA
                 **/
                lstForm: {},

                ledModuleConfiguration: {},

                selectedSerie: '',
                selectedControl: '',
                selectedHousingColor: '',
                selectedOptions: '',
                selectedOptics: '',
                selectedProtectionClass: '',
                generatedSku: '',
                generatedProductName: '',
                calculatedProductPrice: '',
                allLuminaireSelectionStepsSelected: false,

                /**
                 * PROJECT & PLANNING DATA
                 **/
                projects: {},
                projectFormFields: {},

                planningFormFields: {},
                planningCreated: false,
                planningFormErrors: {},

                planning_id: null,

                /**
                 * LUMINATION LINE DATA
                 **/

            filteredLuminaireLineProducts: {},
            isIndividualOpticSelected: false,
            selectedOpticsPartial: null,

                luminaireLineProducts: {},

                luminaireLineFormFields: {},
                luminaireLineFormErrors: {},

                finalPlanningPrice: null,
                luminaireLinePrices: null,

                editPlanning: true,

                lltDataLoaded: false,
                // Monotonic token used to ignore stale catalog responses.
                productsRequestToken: 0,

            }
        },

        created() {
            this.getPlanning();
            this.updateLltDataLoaded();
        },

        methods: {

            /**************************************************************************
             * GENERAL METHODS
             **************************************************************************/

            goToStep(stepToGo) {

                let stepToGoToIsValidated = this.clickableSteps.includes(stepToGo);

                if ( stepToGoToIsValidated ) {
                    this.current_step = stepToGo;
                } else if ( stepToGo <= this.max_step ) {
                    this.current_step = stepToGo;
                }

            },

            goToTab(event, nextStepId) {
                $('#' + nextStepId + '-tab').tab('show');
            },

            /**************************************************************************
             * UPDATE STATES
             * StepNavigation, FormNavigation
             **************************************************************************/

            updateCurrentStep(value) {
                this.current_step = value;
            },

            updateLoading(value) {
                this.loading = value;
            },

            setPlanningId(value) {
                this.planning_id = value;
            },

            updatePlanningFormErrors(value) {
                this.planningFormErrors = value;
            },

            updatePlanningCreated(value) {
                this.planningCreated = value;
            },

            updateLuminaireLineProducts(value) {
                this.luminaireLineProducts = value;
            },

            updateAllLuminaireSelectionStepsSelected(value) {
                this.allLuminaireSelectionStepsSelected = value;
                console.log('All luminaire selection steps selected:', value);
            },

            updateValidatedSteps(value) {

                // Step 1
                if ( value === 'step_1_not_valid' ) {
                    this.validatedSteps = this.validatedSteps.filter(el => el !== 1);
                    // If step 1 is invalid, step 2 and 3 should also be invalid
                    this.validatedSteps = this.validatedSteps.filter(el => el !== 2);
                    this.validatedSteps = this.validatedSteps.filter(el => el !== 3);
                } else if (value === 'step_1_valid') {
                    this.validatedSteps.indexOf(1) === -1 ? this.validatedSteps.push(1) : console.log("Step 1 valid");
                    // Re-evaluate LLT gate as soon as Step 1 becomes valid
                    this.updateLltDataLoaded();
                }

                // Step 2
                if ( value === 'step_2_not_valid' ) {
                    this.validatedSteps = this.validatedSteps.filter(el => el !== 2);
                    // If step 2 is invalid, step 3 should also be invalid
                    this.validatedSteps = this.validatedSteps.filter(el => el !== 3);
                } else if (value === 'step_2_valid') {
                    this.validatedSteps.indexOf(2) === -1 ? this.validatedSteps.push(2) : console.log("Step 2 valid");
                }

                // Step 3
                if ( value === 'step_3_not_valid' ) {
                    this.validatedSteps = this.validatedSteps.filter(el => el !== 3);
                } else if (value === 'step_3_valid') {
                    this.validatedSteps.indexOf(3) === -1 ? this.validatedSteps.push(3) : console.log("Step 3 valid");
                }

                // tempLED RayRail has no step 1
                if (this.product_configurator_id === 3) {
                    this.validatedSteps.push(1);
                }
                // EDIT - Step 2 is pre-filled and valid only if step 1 is valid
                if (this.validatedSteps.includes(1) && !this.validatedSteps.includes(2)) {
                    this.validatedSteps.push(2);
                }

            },

            updateLltDataLoaded() {

                const hasResolvedLedPrice = (
                    this.calculatedProductPrice !== ''
                    && this.calculatedProductPrice !== null
                    && typeof this.calculatedProductPrice !== 'undefined'
                    // LedPriceService returns exact `false` when SKU resolution fails.
                    && this.calculatedProductPrice !== false
                );

                if (this.product_configurator_id === 1
                    && this.filteredLuminaireLineProducts
                    && this.planningFormFields
                    && this.selectedSerie
                    && this.selectedControl
                    && this.selectedHousingColor
                    && this.selectedOptions
                    && this.selectedOptics
                    && this.selectedProtectionClass
                    && this.generatedSku
                    && this.generatedProductName
                    && hasResolvedLedPrice
                ) {
                        this.lltDataLoaded = true;
                } else if (this.product_configurator_id === 3
                    && this.filteredLuminaireLineProducts
                    && this.planningFormFields
                    && this.selectedSerie
                    && this.selectedHousingColor
                    && this.generatedProductName
                ) {
                        this.lltDataLoaded = true;
                }  else {

                        this.lltDataLoaded = false;
                }
            },

            /**************************************************************************
             * UPDATE STATES
             * LuminaireSelectionTool
             **************************************************************************/

            updateLstForm(value) {
                this.loadProductsForLlt();
                this.lstForm = value;
            },
            updateSelectedSerie(value) {
                this.selectedSerie = value;
            },
            updateSelectedControl(value) {
                this.selectedControl = value;
            },
            updateSelectedHousingColor(value) {
                this.selectedHousingColor = value;
            },
            updateSelectedOptions(value) {
                this.selectedOptions = value;
            },
            updateSelectedOptics(value) {
                this.selectedOptics = value;
            },
            updateSelectedOpticsPartial(value) {
                this.selectedOpticsPartial = value;
            },
            updateIndividualOpticSelected(value) {
                this.isIndividualOpticSelected = value;
            },
            updateSelectedProtectionClass(value) {
                this.selectedProtectionClass = value;
            },
            updateGeneratedSku(value) {
                getLedPriceExt.bind(this)(value);
            },
            updateGeneratedProductName(value) {
                this.generatedProductName = value;
            },
            luminaireControlReset(value) {
                if (value) {
                    this.luminaireLineFormFields.number_of_luminaire_controls = 0;
                    this.luminaireLineFormFields.type_of_luminaire_controls = '';

                    this.luminaireLineFormFields.number_of_luminaire_control_slaves = 0;
                    this.luminaireLineFormFields.type_of_luminaire_control_slaves = '';
                }
            },

            /**************************************************************************
             * UPDATE STATES
             * CreateProject
             **************************************************************************/
            updatePlanningFormFields(value) {
                this.planningFormFields = value;
            },

            /**************************************************************************
             * UPDATE STATES
             * LuminaireLineTool
             **************************************************************************/
            updateLuminaireLineFormFields(value) {
                this.luminaireLineFormFields = value;
            },
            updateFinalPlanningPrice(value) {
                this.finalPlanningPrice = value;
            },
            updateLuminaireLinePrices(value) {
                this.luminaireLinePrices = value;
            },

            /**************************************************************************
             * PROJECT METHODS
             **************************************************************************/

            getProjects() {
                if ( this.current_step !== 1 ) this.loading = true;
                axios.get('/api/projects?paginate=0&only_open=1')
                    .then(response => {
                        this.projects = response.data.data;
                        this.loading = false;
                    }).catch(error => {
                        console.log(error);
                        this.loading = false;
                    });
            },

            loadProductsForLlt() {
                // Always load the full catalog; IP55 filtering is handled
                // entirely by FilterProductsService on the frontend side.

                // Guard against out-of-order async responses when edit hydration
                // and watcher updates request catalogs at nearly the same time.
                this.productsRequestToken += 1;
                const requestToken = this.productsRequestToken;

                axios.get('/api/luminaire_line_products?group_by=product_type')
                    .then(response => {
                        if (requestToken !== this.productsRequestToken) {
                            return;
                        }

                        this.products = this.extractCatalogGroups(response?.data || {});
                        this.getFilteredLuminaireLineProducts();
                    })
                    .catch(error => {
                        if (requestToken !== this.productsRequestToken) {
                            return;
                        }
                        console.log(error);
                    });
            },

            extractCatalogGroups(payload) {
                if (
                    payload
                    && typeof payload === 'object'
                    && payload.data
                    && typeof payload.data === 'object'
                    && !Array.isArray(payload.data)
                ) {
                    return payload.data;
                }

                return payload || {};
            },


            /**************************************************************************
             * EDIT PLANNING
             **************************************************************************/

            getPlanning() {
                this.loading = true;
                axios.get('/api/plannings/' + this.$route.params.id + '?product_configurator_id=' + this.product_configurator_id + '&paginate=0')
                    .then(response => {
                        this.planningFormFields = response.data.data;

                        this.product_configurator_id = this.planningFormFields.product_configurator_id;

                        if (this.product_configurator_id === 1) {
                            this.ledModuleConfiguration = {
                                led_module_serie_id: this.planningFormFields.led_module_serie_id,
                                led_module_optics_id: this.planningFormFields.led_module_optics_id,
                                led_module_ambient_temperature_id: this.planningFormFields.led_module_ambient_temperature_id,
                                led_module_power_id: this.planningFormFields.led_module_power_id,
                                led_module_control_id: this.planningFormFields.led_module_control_id,
                                led_module_housing_color_id: this.planningFormFields.led_module_housing_color_id,
                                led_module_color_temperature_id: this.planningFormFields.led_module_color_temperature_id,
                                led_module_color_rendering_index_id: this.planningFormFields.led_module_color_rendering_index_id,
                                led_module_protection_class_id: this.planningFormFields.led_module_protection_class_id,
                            };

                            this.selectedHousingColor = this.planningFormFields.led_module_housing_color_id ? this.planningFormFields.led_module_housing_color_id : '';
                            this.selectedOptics = this.planningFormFields.led_module_optics_id ? this.planningFormFields.led_module_optics_id : '';
                            this.generatedSku = this.planningFormFields.sku_of_led_module ? this.planningFormFields.sku_of_led_module : '';
                            this.generatedProductName = this.planningFormFields.led_module_name ? this.planningFormFields.led_module_name : '';
                        }

                        if (this.product_configurator_id === 3) {
                            this.getProjects();
                            this.updateSelectedSerie('eco');
                            this.updateSelectedHousingColor(3); // 3 = Aluminum bare
                            this.generatedProductName = 'tempLED RayRail';
                            this.validatedSteps.push(1);
                            this.validatedSteps.push(2);
                            this.current_step = 3;
                            this.max_step = 3;
                        }

                        this.updateLltDataLoaded();


                    }).catch(error => {
                        console.log(error);
                    });
            },

            getFilteredLuminaireLineProducts() {

                this.filteredLuminaireLineProducts = getFilteredLuminaireLineProductsExt(this);

                this.updateLltDataLoaded();
            },

        },

        computed: {

            ...mapGetters({
                authenticated: 'auth/authenticated',
                user: 'auth/user',
                permissions: 'auth/permissions'
            }),

            /**
             * GENERAL COMPUTED
             **/

            clickableSteps() {

                let clickableSteps = [];

                // all validated Steps are clickable

                clickableSteps = clickableSteps.concat(this.validatedSteps);
                // Current Step is clickable
                if ( clickableSteps.indexOf(this.current_step) === -1 ) {
                    clickableSteps.push(this.current_step);
                }

                // if current step is validated, next step get clickable
                if ( this.validatedSteps.includes(this.current_step) ) {
                    if ( clickableSteps.indexOf(this.current_step + 1) === -1 ) clickableSteps.push(this.current_step +1);
                }

                // if current step is not validated & current step is 2 or 3 -> previous step is clickable -> GO BACK one Step
                if ( !this.validatedSteps.includes(this.current_step) && this.current_step > 1 ) {
                    if ( clickableSteps.indexOf(this.current_step - 1 ) === -1) clickableSteps.push(this.current_step -1 );
                }

                // if step max step = 3 & step 1 & 2 validated -> step 3
                if (this.max_step === 3 && this.validatedSteps.includes(1) && this.validatedSteps.includes(2)) {
                    if ( clickableSteps.indexOf(3) === -1 ) clickableSteps.push(3);
                }

                // Set max step to highest current step
                if ( this.max_step < this.current_step ) {
                    this.max_step = this.current_step;
                }

                return clickableSteps;
            },

        },

        watch: {
            selectedSerie: function(value) {
                this.loadProductsForLlt();
            },

            generatedSku: function(value) {
                if (value) {
                    this.getFilteredLuminaireLineProducts();
                }
            },

            selectedProtectionClass: function() {
                this.loadProductsForLlt();
            },

            // When price resolves, re-evaluate LLT prefill gate
            calculatedProductPrice: function (value) {
                this.updateLltDataLoaded();
            }

        },
    }
</script>
